import React, { useCallback, useEffect, useRef, useState } from 'react';
import InvoiceDialog from './InvoiceDialog';
import { getDelivery, recordDelivery, reviewDelivery } from '../services/deliveryApi';
import { deliveryStatus, displayDate, localToday, recordedQuantity, wholeQuantity } from '../utils/delivery';

const inputClass = 'w-full border px-2 py-1 rounded';
const buttonClass = 'border px-3 py-1.5 rounded disabled:opacity-50';
const storageKey = id => `delivery-attempt:${id}`;
function readAttempt(id) {
  try { return JSON.parse(sessionStorage.getItem(storageKey(id))) || null; } catch { return null; }
}
function storeAttempt(id, value) {
  try {
    if (value) sessionStorage.setItem(storageKey(id), JSON.stringify(value));
    else sessionStorage.removeItem(storageKey(id));
  } catch { /* The in-memory attempt still protects retries in this panel. */ }
}
const errorMessage = error => error.response?.data?.error || 'Unable to reach the server. Please try again.';

export default function DeliveryPanel({ invoiceId, onClose, onChanged, onNext }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const lock = useRef(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [quantity, setQuantity] = useState('');
  const [date, setDate] = useState(localToday);
  const [notes, setNotes] = useState('');
  const [actual, setActual] = useState({});
  const [confirming, setConfirming] = useState(false);
  const [attempt, setAttempt] = useState(() => readAttempt(invoiceId));
  const attemptRef = useRef(attempt);

  const clearAttempt = useCallback(() => {
    attemptRef.current = null; setAttempt(null); storeAttempt(invoiceId, null);
  }, [invoiceId]);
  const accept = useCallback(data => {
    setDoc(data); onChanged(data); setActual({}); setConfirming(false);
    if (attemptRef.current && (data.deliveryHistory || []).some(row => row.requestId === attemptRef.current.requestId)) {
      clearAttempt(); setMessage('Delivery recorded successfully.');
    }
  }, [onChanged, clearAttempt]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    getDelivery(invoiceId, { signal: controller.signal }).then(data => { if (active) accept(data); })
      .catch(err => { if (active) setError(errorMessage(err)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; controller.abort(); };
  }, [invoiceId, accept]);

  async function refresh() {
    if (lock.current) return;
    lock.current = true; setLoading(true); setError('');
    try { accept(await getDelivery(invoiceId)); setSelectedId(''); }
    catch (err) { setError(errorMessage(err)); }
    finally { lock.current = false; setLoading(false); }
  }

  const selected = doc?.items.find(item => item._id === selectedId);
  const reviewValid = doc?.items.length > 0 && doc.items.every(item =>
    wholeQuantity(actual[item._id] ?? '', recordedQuantity(doc, item._id), item.qty));

  async function submitDelivery(event) {
    event.preventDefault();
    if (lock.current || loading || !doc?.canManageDelivery) return;
    let payload = attemptRef.current;
    if (!payload) {
      if (!selected || !wholeQuantity(quantity, 1, selected.pendingQty) || !date || !Number.isFinite(new Date(`${date}T00:00:00`).getTime())) {
        setError('Enter a whole delivery quantity within the pending quantity and a valid date.'); return;
      }
      payload = { itemId: selectedId, quantity: Number(quantity), date: new Date(`${date}T00:00:00`).toISOString(),
        notes: notes.trim(), requestId: window.crypto?.randomUUID?.() || `delivery-${Date.now()}-${Math.random().toString(36).slice(2)}` };
      attemptRef.current = payload; setAttempt(payload); storeAttempt(invoiceId, payload);
    }
    lock.current = true; setBusy(true); setError(''); setMessage('');
    try {
      const result = await recordDelivery(invoiceId, payload);
      clearAttempt(); accept(result); setSelectedId(''); setQuantity(''); setNotes('');
      setMessage('Delivery recorded successfully.');
      try { accept(await getDelivery(invoiceId)); }
      catch { setError('Delivery saved, but refresh failed. Reload delivery details before continuing.'); }
    } catch (err) {
      if (err.response?.status >= 400 && err.response.status < 500 && ![408, 429].includes(err.response.status)) {
        clearAttempt();
        if (err.response.status === 409) {
          setSelectedId('');
          try { accept(await getDelivery(invoiceId)); }
          catch { setDoc(null); }
          setError('The invoice changed. Details have been reloaded when available; check quantities before submitting again.');
        } else setError(errorMessage(err));
      } else {
        setError('Delivery outcome is unconfirmed. Use Retry Same Delivery to check or complete this submission safely.');
      }
    } finally { lock.current = false; setBusy(false); }
  }

  async function submitReview() {
    if (lock.current || loading || !reviewValid || !confirming || attemptRef.current) return;
    lock.current = true; setBusy(true); setError(''); setMessage('');
    try {
      const result = await reviewDelivery(invoiceId, { deliveryRevision: doc.deliveryRevision,
        items: doc.items.map(item => ({ itemId: item._id, deliveredQty: Number(actual[item._id]) })) });
      accept(result); setMessage('Delivery quantities confirmed.');
      try { accept(await getDelivery(invoiceId)); }
      catch { setError('Review saved, but refresh failed. Reload delivery details before continuing.'); }
    } catch (err) {
      setConfirming(false);
      try { accept(await getDelivery(invoiceId)); } catch { setDoc(null); }
      setError(err.response?.status === 409
        ? 'The invoice changed. Review every item again using the latest quantities.'
        : `${errorMessage(err)} Review status has been refreshed when available; check before retrying.`);
    } finally { lock.current = false; setBusy(false); }
  }

  const disabled = busy || loading;
  const history = [...(doc?.deliveryHistory || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
  return <InvoiceDialog title="Delivery Management" onClose={onClose} busy={busy}>
    <div className="flex flex-wrap items-center gap-3 mb-4">
      <button className={buttonClass} disabled={disabled} onClick={refresh}>Reload delivery details</button>
      {onNext && <button className={buttonClass} disabled={disabled || !!attempt} onClick={onNext}>Next Pending Invoice</button>}
    </div>
    {loading && <p role="status">Loading delivery details…</p>}
    {message && <p role="status" className="mb-3 rounded bg-emerald-50 text-emerald-800 p-3">{message}</p>}
    {error && <p role="alert" className="mb-3 rounded bg-rose-50 text-rose-800 p-3">{error}</p>}
    {attempt && <div className="border border-amber-300 bg-amber-50 p-3 mb-4 rounded">
      <p>An earlier delivery submission needs confirmation: quantity {attempt.quantity}, dated {displayDate(attempt.date)}. Reload to check, or retry the same submission. New delivery entry is locked until this is resolved.</p>
      <form onSubmit={submitDelivery}><button className={`${buttonClass} mt-2`} disabled={disabled || !doc?.canManageDelivery}>Retry Same Delivery</button></form>
    </div>}
    {doc && <>
      <dl className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4 text-sm">
        <div><dt className="text-gray-500">Invoice</dt><dd>{doc.invoiceNumber || doc._id}</dd></div>
        <div><dt className="text-gray-500">Customer</dt><dd>{doc.customerName}</dd></div>
        <div><dt className="text-gray-500">Mobile / WhatsApp</dt><dd>{doc.whatsapp || '—'}</dd></div>
        <div><dt className="text-gray-500">Organization</dt><dd>{doc.organization || '—'}</dd></div>
        <div><dt className="text-gray-500">Invoice date</dt><dd>{displayDate(doc.invoiceDate)}</dd></div>
        <div><dt className="text-gray-500">Delivery status</dt><dd className="font-semibold">{deliveryStatus(doc)}</dd>{doc.deliveryReviewed && doc.deliveryReviewedAt && <dd className="text-gray-600">Reviewed on {displayDate(doc.deliveryReviewedAt)}</dd>}</div>
      </dl>
      {!doc.deliveryReviewed && <p className="bg-amber-50 text-amber-900 rounded p-3 mb-4 text-sm">Pending Delivery includes historical records. Quantities use recorded item amounts; use the confirmation form below when actual delivered quantities need confirmation.</p>}
      {!doc.canManageDelivery && <p role="alert" className="bg-rose-50 text-rose-800 p-3 mb-4 rounded">{doc.deliveryIssues?.length ? doc.deliveryIssues.join(' ') : 'This invoice needs persisted, unique item IDs before deliveries or verification can be saved.'} Ask an administrator to arrange the historical-data repair. No quantities have been changed.</p>}
      <div className="overflow-x-auto mb-4"><table className="min-w-full text-sm">
        <thead className="bg-gray-50"><tr>{['Item', 'Ordered', 'Delivered', 'Pending', 'Action'].map(label => <th key={label} className="text-left px-3 py-2">{label}</th>)}</tr></thead>
        <tbody>{doc.items.map((item, index) => <tr key={item._id || index} className={`border-t ${doc.deliveryReviewed && item.pendingQty === 0 ? 'bg-emerald-50' : ''}`}>
          <td className="px-3 py-2">{item.label}</td><td className="px-3 py-2">{item.qty}</td><td className="px-3 py-2">{item.deliveredQty}</td><td className="px-3 py-2">{item.pendingQty}</td>
          <td className="px-3 py-2">{item.pendingQty === 0 ? <span className="font-medium">{doc.deliveryReviewed ? 'Fully Delivered' : 'Pending Delivery — confirm quantities below'}</span> : <>
            <span className="text-gray-500 mr-2">{item.deliveredQty > 0 && doc.deliveryReviewed ? 'Partially Delivered' : 'Pending Delivery'}</span>
            <button className="text-indigo-600 underline disabled:opacity-50" disabled={disabled || !doc.canManageDelivery || !!attempt}
              onClick={() => { setSelectedId(item._id); setQuantity(item.pendingQty === 1 ? '1' : ''); setDate(localToday()); setNotes(''); setError(''); setMessage(''); }}>{item.pendingQty === 1 ? 'Deliver Remaining' : 'Deliver'}<span className="sr-only"> {item.label}</span></button>
          </>}</td>
        </tr>)}</tbody>
      </table></div>
      {selected && !attempt && <form onSubmit={submitDelivery} className="border rounded p-4 mb-4">
        <h3 className="font-semibold mb-3">Record delivery — {selected.label}</h3>
        <fieldset disabled={disabled} className="grid md:grid-cols-3 gap-3">
          <label>Delivery quantity<input autoFocus aria-label="Delivery quantity" type="number" required min="1" max={selected.pendingQty} step="1" className={inputClass} value={quantity} onChange={e => setQuantity(e.target.value)} /></label>
          <label>Delivery date<input type="date" required className={inputClass} value={date} onChange={e => setDate(e.target.value)} /></label>
          <label>Delivery notes (optional)<input className={inputClass} value={notes} onChange={e => setNotes(e.target.value)} /></label>
          <div className="md:col-span-3 flex flex-wrap gap-2">
            <button type="button" className={buttonClass} onClick={() => setQuantity(String(selected.pendingQty))}>Deliver Remaining</button>
            <button type="submit" className="bg-indigo-600 text-white px-3 py-1.5 rounded disabled:opacity-50" disabled={!wholeQuantity(quantity, 1, selected.pendingQty) || !date}>Save Delivery</button>
            <button type="button" className={buttonClass} onClick={() => setSelectedId('')}>Cancel</button>
          </div>
        </fieldset>
      </form>}
      {!doc.deliveryReviewed && <section className="border rounded p-4 mb-4">
        <h3 className="font-semibold mb-2">Confirm Delivery Quantities</h3>
        <p className="text-sm text-gray-600 mb-3">Enter the actual cumulative delivered quantity for every item. Zero confirms not delivered; ordered quantity confirms fully delivered. No historical delivery transactions or dates will be created.</p>
        <fieldset disabled={disabled || !doc.canManageDelivery || !!attempt || confirming} className="grid md:grid-cols-2 gap-3">
          {doc.items.map((item, index) => <label key={item._id || index} className="text-sm">{item.label} — actual delivered (of {item.qty})
            <input className={inputClass} aria-label={`Actual delivered for ${item.label}`} type="number" min={recordedQuantity(doc, item._id)} max={item.qty} step="1" required value={actual[item._id] ?? ''}
              onChange={e => setActual(old => ({ ...old, [item._id]: e.target.value }))} />
            <span className="text-gray-500">Minimum supported by delivery history: {recordedQuantity(doc, item._id)}</span>
          </label>)}
        </fieldset>
        {!confirming ? <button className={`${buttonClass} mt-3`} disabled={disabled || !doc.canManageDelivery || !reviewValid || !!attempt} onClick={() => setConfirming(true)}>Review Entered Quantities</button>
          : <div className="mt-3 bg-gray-50 p-3 rounded">
            <h4 className="font-medium">Confirm actual delivered quantities</h4>
            <ul className="my-2">{doc.items.map(item => <li key={item._id}>{item.label}: {actual[item._id]} delivered / {item.qty} ordered</li>)}</ul>
            <div className="flex gap-2"><button className="bg-indigo-600 text-white px-3 py-1.5 rounded disabled:opacity-50" disabled={disabled || !reviewValid} onClick={submitReview}>Confirm Actual Delivered Quantities</button>
              <button className={buttonClass} disabled={disabled} onClick={() => setConfirming(false)}>Back to Quantities</button></div>
          </div>}
      </section>}
      <section><h3 className="font-semibold mb-2">Delivery History</h3>
        {doc.deliveryReviewed && <p className="text-sm bg-gray-50 p-3 rounded mb-3">Historical verification / invoice review: {displayDate(doc.deliveryReviewedAt)}. This is the verification date, not a historical delivery date.</p>}
        {!history.length ? <p className="text-sm text-gray-500">No recorded delivery transactions.</p> : <div className="overflow-x-auto"><table className="min-w-full text-sm">
          <thead className="bg-gray-50"><tr>{['Delivery date', 'Item', 'Quantity', 'Notes'].map(label => <th key={label} className="text-left px-3 py-2">{label}</th>)}</tr></thead>
          <tbody>{history.map((row, index) => <tr key={row._id || index} className="border-t"><td className="px-3 py-2">{displayDate(row.date)}</td><td className="px-3 py-2">{doc.items.find(item => String(item._id).toLowerCase() === String(row.itemId).toLowerCase())?.label || `Item ${row.itemId}`}</td><td className="px-3 py-2">{row.quantity}</td><td className="px-3 py-2 whitespace-pre-wrap">{row.notes || '—'}</td></tr>)}</tbody>
        </table></div>}
      </section>
    </>}
  </InvoiceDialog>;
}
